// User-side view of published shortlists (mini-PRD: mobile shortlist viewing
// for care circles). Mirrors the CAM-side care search world in
// pages/CareSearch.tsx — same providers, statuses and notes — so the two
// prototypes tell one story.

export type ProviderStatus =
  'Not contacted' | 'Contacted' | 'Secured' | 'Available' | 'Unavailable';

// Chip colours per status (same mapping as the CAM side): Secured is the
// prominent green the family can act on; Unavailable is clearly marked.
// Available reads the same as Secured — it is a provider the family can act
// on — and is used where a provider has confirmed they can help but nothing
// has been agreed yet (see the blurred-profile paywall exploration).
export const STATUS_COLOR: Record<ProviderStatus, string> = {
  'Not contacted': 'default',
  Contacted: 'info',
  Secured: 'success',
  Available: 'success',
  Unavailable: 'warmOrange',
};

export type InspectionArea = { area: string; rating: string };

export type ShortlistProvider = {
  id: string;
  name: string;
  address: string;
  distance: string;
  phone: string;
  email: string;
  website: string;
  price: string;
  rating: 'Outstanding' | 'Good' | 'Requires improvement';
  careTypes: string[];
  services: string[];
  features: string[];
  status: ProviderStatus;
  // The CAM's shortlist note, visible to the care circle.
  note?: string;
  beds: number;
  availability: string;
  manager: string;
  visiting: string;
  ownerQuote: string;
  description: string;
  funding: string;
  lastInspected: string;
  inspection: InspectionArea[];
};

export type PublishedShortlist = {
  id: string;
  name: string;
  updated: string; // 'Updated 28th May'
  providers: ShortlistProvider[]; // in the CAM's published order
};

const INSPECTION_GOOD: InspectionArea[] = [
  { area: 'Safe', rating: 'Good' },
  { area: 'Effective', rating: 'Good' },
  { area: 'Caring', rating: 'Outstanding' },
  { area: 'Responsive', rating: 'Good' },
  { area: 'Well-led', rating: 'Good' },
];

const OAKWOOD: ShortlistProvider = {
  id: 'oakwood',
  name: 'Oakwood care home',
  address: '14 Brae Street, Edinburgh EH3 6HR',
  distance: '0.8 miles',
  phone: '0131 555 0299',
  email: 'hello@oakwoodcare.co.uk',
  website: 'oakwoodcare.co.uk',
  price: '£1,250/week',
  rating: 'Good',
  careTypes: ['Residential', 'Nursing', 'Dementia', 'Respite'],
  services: ['Palliative care', 'Physiotherapy', 'Day trips', 'Hairdressing'],
  features: [
    'En-suite rooms',
    'Landscaped gardens',
    'On-site physiotherapy',
    'Pet friendly',
  ],
  status: 'Secured',
  note: 'Two rooms free on the ground floor and the manager has held one for Mum until the end of the month. My top pick. Happy to talk it through.',
  beds: 32,
  availability: 'Available',
  manager: 'Sarah Mbeki',
  visiting: 'Open visiting',
  ownerQuote:
    '“We’ve cared for our local community for over 20 years, and every member of our team treats residents like family.”',
  description:
    'Oakwood is a purpose-built home offering residential, nursing and specialist dementia care across two landscaped acres. Our on-site clinical team supports complex needs, and a varied activities programme keeps residents engaged.',
  funding:
    'Accepts local authority funding with a third-party top-up. Self-funded rate includes all care and activities; nursing care is funded via FNC where eligible.',
  lastInspected: 'Jan 2026',
  inspection: INSPECTION_GOOD,
};

const RIVERSIDE: ShortlistProvider = {
  id: 'riverside',
  name: 'Riverside lodge',
  address: '2 Mill Lane, Edinburgh EH4 1AB',
  distance: '1.4 miles',
  phone: '0131 555 1190',
  email: 'hello@riversidelodge.co.uk',
  website: 'riversidelodge.co.uk',
  price: '£1,320/week',
  rating: 'Good',
  careTypes: ['Nursing', 'Dementia'],
  services: ['Palliative care', 'Sensory garden', 'Music therapy'],
  features: ['River views', 'En-suite rooms', 'Quiet lounges'],
  status: 'Contacted',
  note: 'Receptionist is unsure if the space available has already been reserved and will get back to me before the end of the week.',
  beds: 24,
  availability: 'Checking',
  manager: 'Tom Crawford',
  visiting: 'Open visiting',
  ownerQuote:
    '“Our riverside setting gives residents calm, and our nursing team gives families confidence.”',
  description:
    'Riverside lodge specialises in nursing and dementia care in a quiet riverside setting. Smaller and more intimate than most homes in the area, with a high staff-to-resident ratio.',
  funding:
    'Self-funded and local authority placements. NHS continuing healthcare accepted for eligible residents.',
  lastInspected: 'Mar 2026',
  inspection: [
    { area: 'Safe', rating: 'Good' },
    { area: 'Effective', rating: 'Good' },
    { area: 'Caring', rating: 'Good' },
    { area: 'Responsive', rating: 'Good' },
    { area: 'Well-led', rating: 'Good' },
  ],
};

const CHERRY_TREE: ShortlistProvider = {
  id: 'cherry-tree',
  name: 'Cherry Tree house',
  address: '88 Orchard Road, Edinburgh EH5 2QP',
  distance: '2.1 miles',
  phone: '0131 555 7723',
  email: 'hello@cherrytreehouse.co.uk',
  website: 'cherrytreehouse.co.uk',
  price: '£1,480/week',
  rating: 'Outstanding',
  careTypes: ['Residential'],
  services: ['Activities programme', 'Gardening club', 'Visiting GP'],
  features: ['Orchard grounds', 'Private dining room', 'Cinema room'],
  status: 'Not contacted',
  beds: 40,
  availability: 'Waitlist',
  manager: 'Helen Forsyth',
  visiting: 'Booked visits',
  ownerQuote:
    '“Outstanding isn’t a badge for us. It’s the standard our residents deserve every day.”',
  description:
    'Cherry Tree house is a residential home rated Outstanding by the regulator, set in mature orchard grounds with an exceptional activities programme.',
  funding:
    'Primarily self-funded. A small number of local authority places with top-up.',
  lastInspected: 'Nov 2025',
  inspection: [
    { area: 'Safe', rating: 'Outstanding' },
    { area: 'Effective', rating: 'Good' },
    { area: 'Caring', rating: 'Outstanding' },
    { area: 'Responsive', rating: 'Outstanding' },
    { area: 'Well-led', rating: 'Good' },
  ],
};

const BRAMBLE: ShortlistProvider = {
  id: 'bramble',
  name: 'Bramble court',
  address: '5 Hillview Terrace, Edinburgh EH6 8LN',
  distance: '3.0 miles',
  phone: '0131 555 4401',
  email: 'hello@bramblecourt.co.uk',
  website: 'bramblecourt.co.uk',
  price: '£1,180/week',
  rating: 'Good',
  careTypes: ['Residential', 'Dementia'],
  services: ['Day care', 'Respite stays', 'Chiropody'],
  features: ['Hillside views', 'Accessible gardens'],
  status: 'Unavailable',
  note: 'Spoke to the manager. No beds until autumn and the waitlist is long. Keeping it here so you can see it was considered.',
  beds: 28,
  availability: 'No beds until autumn',
  manager: 'Aileen Ross',
  visiting: 'Open visiting',
  ownerQuote:
    '“A smaller home with a big heart. Most of our team have been with us for over a decade.”',
  description:
    'Bramble court is a friendly residential home on the hillside with strong community links and specialist dementia support.',
  funding: 'Local authority and self-funded placements.',
  lastInspected: 'Aug 2025',
  inspection: INSPECTION_GOOD,
};

export const SHORTLISTS: PublishedShortlist[] = [
  // Published and empty, which under the paywall is what a shortlist looks like
  // for its first hours: the care expert has sent the request to every provider
  // in the area and none of them has confirmed yet. The family sees the list
  // exists before it has anybody on it, so the waiting has to be said out loud
  // rather than left as a blank screen.
  {
    id: 'awaiting-providers',
    name: 'Home care in Edinburgh',
    updated: 'Updated today',
    providers: [],
  },
  {
    id: 'residential-nursing',
    name: 'Residential with nursing',
    updated: 'Updated 28th May',
    providers: [OAKWOOD, RIVERSIDE, CHERRY_TREE, BRAMBLE],
  },
  {
    id: 'dementia-specialists',
    name: 'Dementia specialists',
    updated: 'Updated 21st May',
    providers: [RIVERSIDE, BRAMBLE],
  },
];

/**
 * A shortlist, and a provider inside it, by id.
 *
 * The handover pages used to reach for these by position: SHORTLISTS[0] with a
 * comment saying which list that was. An empty shortlist then went in at the
 * front, every provider came back undefined, and the pages rendering them went
 * white on `provider.status`. Nothing said so, because those pages had no link
 * pointing at them at the time. Ids do not move.
 */
export const shortlistById = (id: string): PublishedShortlist => {
  const found = SHORTLISTS.find((s) => s.id === id);
  if (!found) throw new Error(`No shortlist fixture with id "${id}"`);
  return found;
};

export const providerById = (
  shortlistId: string,
  providerId: string,
): ShortlistProvider => {
  const found = shortlistById(shortlistId).providers.find(
    (p) => p.id === providerId,
  );
  if (!found) {
    throw new Error(
      `No provider "${providerId}" on shortlist "${shortlistId}"`,
    );
  }
  return found;
};
